'use client'
import { Button } from '@achieve/ascend'
import { Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import { PhoneCall } from 'react-feather'
import { useEffect, useRef, useState } from 'react'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { MOBILE_APP_DOWNLOAD_FDR_ROUTE } from 'constants/navigation'
import { Image } from '@/components/Image'
import { useViewportSmallerThan } from '@/utils/viewport'
import { BREAKPOINTS } from '@/constants/viewport'
import { TrackingWrapper } from '@/components/Tracking/TrackingWrapper'
import Cookies from 'js-cookie'
import { CHATBOT_ANALYTICS_MAPPING, VARIATIONS_UPPER } from '@/constants'
import dynamic from 'next/dynamic'
import { WEBSITE_NAMES } from '@/constants/websites'
import { getPageContentTypes } from '@/lib/getPageContentType'
import classNames from 'classnames'
const ChatController = dynamic(() => import('@/components/Chatbot/ChatController'), {
  ssr: false,
})

export const STICKY_FOOTER_BLACKLIST = [
  '/',
  '/contact/',
  '/l/[slug]',
  '/lp/[slug]',
  '/a/[slug]',
  '/privacy/policies',
  '/privacy/terms',
  '/privacy/privacy-policy',
  '/privacy/freedom-debt-relief-california-privacy-disclosures',
  '/privacy/freedom-debt-relief-california-privacy-disclosures-es',
  '/privacy/changes-to-program/',
  '/privacy/es/cambios-en-el-programa/',
  '/about/partners',
  '/campaigns/10k-start-fresh',
  MOBILE_APP_DOWNLOAD_FDR_ROUTE,
]

export default function FooterStickyChatbot({
  siteEnv,
  predefinedChatPrompts,
  isConsentBannerVisible,
  associatedProduct,
  phoneData,
  chatbotVariation,
}) {
  const isVariationB = chatbotVariation === VARIATIONS_UPPER.VARIATION_B
  const isMobile = useViewportSmallerThan(BREAKPOINTS.md)
  const { websiteField } = getPageContentTypes()

  const [menuCondensed] = useLayoutMenuCondensed()
  const { dynamicPathname } = useAppRouter()
  const [visible, setVisible] = useState(menuCondensed)
  const [chatOpen, setChatOpen] = useState(false)
  const trackRefInView = useRef()
  const trackRefOpenChat = useRef()
  // Track last known condensed state
  const lastCondensedRef = useRef(menuCondensed)

  useEffect(() => {
    if (!chatOpen) {
      // only update when chat not open
      setVisible(menuCondensed)
      lastCondensedRef.current = menuCondensed
    }
  }, [menuCondensed, chatOpen])

  // Lock body scroll when chat is open on mobile
  useBodyScrollLock(chatOpen && isMobile, {
    preserveScrollPosition: true,
    disableTouchActions: true,
  })

  useEffect(() => {
    if (!Cookies.get('chatbot_visible_experiment') && (!isMobile || visible)) {
      trackRefInView?.current?.click()
      Cookies.set('chatbot_visible_experiment', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, visible])

  if (STICKY_FOOTER_BLACKLIST.includes(dynamicPathname)) return null

  const handleCall = () => {
    if (phoneData?.phoneNumber) window.open(`tel:${phoneData.phoneNumber}`, '_self')
  }

  const isGreenDesign = [WEBSITE_NAMES.FDR, WEBSITE_NAMES.BILLS].includes(websiteField)

  return (
    <>
      {/* Event trigger for chat into view  */}
      <TrackingWrapper
        data-testid="chat-experiment-in-view"
        track={{
          list_name: 'AIChatbot_Impression',
          event_type: 'track',
          event_action: 'AIChatbot_Impression',
          nav_link_section: CHATBOT_ANALYTICS_MAPPING[siteEnv],
          track_event: 'chat_experiment_start',
        }}
      >
        <div ref={trackRefInView} />
      </TrackingWrapper>

      {/* Event trigger for Open chat  */}
      <TrackingWrapper
        data-testid="chat-experiment-open-chat"
        track={{
          list_name: 'AIChatbot_Click',
          event_type: 'track',
          event_action: 'AIChatbot_Click',
          click_type: 'button_click',
          nav_link_section: CHATBOT_ANALYTICS_MAPPING[siteEnv],
          track_event: 'chat_experiment_start',
          click_text: 'Chat with us',
        }}
      >
        <div ref={trackRefOpenChat} />
      </TrackingWrapper>

      {/* Sticky footer mobile section with chatbot*/}
      <div
        className={`
    flex items-center justify-center z-50
    bg-white shadow-[0_-1px_3px_rgba(0,0,0,0.08)]
    ${websiteField === WEBSITE_NAMES.FDR ? 'h-[68px] px-4 gap-[20px]' : 'h-[80px] gap-[14px]'} ${
          visible && !isVariationB ? 'block' : 'hidden'
        }
    breakpointMd:hidden
  `}
      >
        {phoneData?.phoneNumber && (
          <div className="col-span-3">
            <AchieveLink
              noLink
              track={{
                list_name: 'CALL STICKY FOOTER',
                click_id: phoneData?.phoneNumber,
                click_text: `Call Us`,
                click_url: phoneData?.phoneNumber,
                click_type: 'Call Us Click',
                event_action: 'button_click',
                nav_link_section: 'Sticky Footer',
                track_event: 'call-sticky-footer',
              }}
              data-testid="call-sticky-footer"
            >
              {websiteField === WEBSITE_NAMES.FDR ? (
                <Button
                  color="secondary"
                  variant={isGreenDesign ? 'outlined' : 'contained'}
                  className={classNames('w-full px-4 py-3', {
                    'h-[52px]': isGreenDesign,
                  })}
                  onClick={handleCall}
                >
                  <Typography variant="displayXs" fontWeight="bold" content="Call Us" />
                </Button>
              ) : (
                <Button
                  onClick={handleCall}
                  className="gap-2 rounded-md p-4 flex items-center justify-center bg-content-accent hover:bg-[#082178] border-0 hover:cursor-pointer h-[56px]"
                >
                  <PhoneCall size={24} className="text-content-inverse-primary" />
                  <Typography
                    variant="labelLg"
                    fontWeight="bold"
                    className="text-content-inverse-primary"
                    content="Call Us"
                  />
                </Button>
              )}
            </AchieveLink>
          </div>
        )}
        {/* Chat Button */}
        <Button
          color="primary"
          variant="contained"
          className={`flex items-center gap-2 rounded-md p-4 bg-content-accent ${
            websiteField === WEBSITE_NAMES.FDR
              ? 'flex-1 h-[52px] hover:bg-[#082178]'
              : 'h-[56px] bg-content-accent-light'
          }`}
          onClick={() => {
            setChatOpen(true)
            trackRefOpenChat?.current?.click()
          }}
        >
          <Image
            alt="ai sparkle"
            height={24}
            width={24}
            color="blue"
            src={
              websiteField === WEBSITE_NAMES.FDR
                ? '/multi-site-assets/ai-sparkle.svg'
                : '/multi-site-assets/ai-sparkle-dark.svg'
            }
          />
          <Typography
            variant="labelLg"
            fontWeight="bold"
            className={
              websiteField === WEBSITE_NAMES.FDR
                ? 'text-content-inverse-primary'
                : 'text-content-accent'
            }
            content="Chat with us"
          />
        </Button>
      </div>
      {/* Desktop floating button*/}
      <div
        className={`w-full max-w-[1200px] flex justify-self-center justify-end fixed ${
          isVariationB
            ? `pr-2 z-51 ${
                isConsentBannerVisible ? 'bottom-[310px] lg:bottom-[140px]' : 'bottom-[108px]'
              }`
            : `pr-2 z-10 ${isConsentBannerVisible ? 'bottom-[160px]' : 'bottom-[45px]'}`
        } ${visible ? 'block' : 'hidden'}`}
        data-testid="desktop-chat-bubble"
      >
        <div
          className={`items-center justify-center bg-white shadow-[1px_5px_9.9px_0px_rgba(20,46,87,0.05),0px_13px_32px_0px_rgba(29,37,47,0.0776)] rounded-[36px] w-[200px] h-[70px] border-[#D2E2FE] border-solid border ${
            chatOpen ? 'hidden' : isVariationB ? 'flex' : 'hidden breakpointMd:flex'
          }`}
        >
          <Button
            color="primary"
            variant="contained"
            className="flex items-center bg-content-accent w-[170px] h-[46px] rounded-[50px] border-none p-0 gap-1 font-bold text-lg hover:bg-[#082178]"
            onClick={() => {
              setChatOpen(true)
              trackRefOpenChat?.current?.click()
            }}
          >
            <Image
              alt="ai sparkle"
              height={24}
              width={24}
              src={
                websiteField === WEBSITE_NAMES.FDR
                  ? '/multi-site-assets/ai-sparkle.svg'
                  : '/multi-site-assets/ai-sparkle-desktop.svg'
              }
            />
            <Typography
              variant="labelLg"
              fontWeight="bold"
              className="text-[#F9F9F9]"
              content="Chat with us"
            />
          </Button>
        </div>
      </div>

      <ChatController
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        siteEnv={siteEnv}
        predefinedChatPrompts={predefinedChatPrompts}
        associatedProduct={associatedProduct}
        isConsentBannerVisible={isConsentBannerVisible}
      />
    </>
  )
}
